import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PODIUM_URL } from "@/lib/assets";

const initial = { name: "", email: "", organization: "", phone: "", event_type: "", event_date: "", event_location: "", audience_size: "", budget_range: "", topic: "", message: "" };

export default function Book() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/speaking-requests", form);
      setDone(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-cream">
      <section className="border-b border-gold-light">
        <div className="brand-container py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="brand-eyebrow">Speaking & Appearances</div>
            <h1 className="brand-h1 mt-3">Book Judge Auntie™.</h1>
            <p className="brand-lede mt-6">Keynotes, panels, fireside conversations, destination weddings, and spiritual and educational appearances — bring the bench and the front porch to your event.</p>
          </div>
          <div className="lg:col-span-6">
            <img src={PODIUM_URL} alt="Judge Olds speaking" className="w-full h-[380px] object-cover rounded-lg border border-gold-light" />
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-3xl mx-auto px-6 py-16">
          {done ? (
            <div className="editorial-card p-10 text-center">
              <div className="brand-eyebrow">Message received</div>
              <h2 className="brand-h2 mt-3">Thank you.</h2>
              <p className="mt-4 text-charcoal/70">Judge Auntie's™ team will review your request and follow up soon. A confirmation is on its way to your inbox.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="editorial-card p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Your name" value={form.name} onChange={set("name")} required testId="book-name" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} required testId="book-email" />
                <Field label="Organization" value={form.organization} onChange={set("organization")} testId="book-org" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} testId="book-phone" />
                <SelectField
                  label="Event type"
                  value={form.event_type}
                  onChange={set("event_type")}
                  required
                  testId="book-type"
                  options={[
                    "Keynote",
                    "Panel",
                    "Fireside conversation",
                    "Destination wedding",
                    "Spiritual appearance",
                    "Educational appearance",
                    "Other",
                  ]}
                />
                <Field label="Event date" value={form.event_date} onChange={set("event_date")} placeholder="Approx." testId="book-date" />
                <Field label="Event location" value={form.event_location} onChange={set("event_location")} testId="book-location" />
                <Field label="Audience size" value={form.audience_size} onChange={set("audience_size")} placeholder="e.g., 200–500" testId="book-audience" />
                <Field label="Budget range" value={form.budget_range} onChange={set("budget_range")} testId="book-budget" />
                <Field label="Topic focus" value={form.topic} onChange={set("topic")} testId="book-topic" />
              </div>
              <div>
                <label className="text-sm text-charcoal/70">Tell Auntie about your event *</label>
                <textarea rows={5} required value={form.message} onChange={set("message")} data-testid="book-message"
                  className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
              </div>
              <button disabled={busy} className="btn-primary" data-testid="book-submit">{busy ? "Sending…" : "Send booking inquiry"}</button>
              <p className="text-xs text-charcoal/50">By submitting you agree to be contacted by Judge Auntie's™ team regarding this booking request.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testId }) {
  return (
    <div>
      <label className="text-sm text-charcoal/70">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} data-testid={testId}
        className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required, testId }) {
  return (
    <div>
      <label className="text-sm text-charcoal/70">{label}{required && " *"}</label>
      <select value={value} onChange={onChange} required={required} data-testid={testId}
        className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold appearance-none bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2212%22%20height=%2212%22%20viewBox=%220%200%2024%2024%22%20fill=%22none%22%20stroke=%22%235B1A2E%22%20stroke-width=%222%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22><polyline%20points=%226%209%2012%2015%2018%209%22></polyline></svg>')] bg-no-repeat bg-[right_1rem_center] pr-10">
        <option value="" disabled data-testid={`${testId}-placeholder`}>Select an event type…</option>
        {options.map((opt) => (
          <option key={opt} value={opt} data-testid={`${testId}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{opt}</option>
        ))}
      </select>
    </div>
  );
}
